import { useCallback, useEffect, useMemo, useState } from 'react';
import { FlaskConical, Loader2, Mic2, Plus, Store as StoreIcon, Tags, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { labApi, storeApi, type Store } from '../lib/api';
import { Modal } from '../components/ui/Modal';
import { Select } from '../components/ui/Select';

interface LabBrand {
  id: string;
  name: string;
  productCount: number;
  store: {
    id: string;
    name: string;
    engineEnabled: boolean;
  } | null;
}

export function LabBrandsPage({ mode = 'page', onCreated }: { mode?: 'page' | 'button' | 'grid'; onCreated?: () => void }) {
  const [brands, setBrands] = useState<LabBrand[]>([]);
  const [unbrandedCount, setUnbrandedCount] = useState(0);
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [storeId, setStoreId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [brandResponse, storeResponse] = await Promise.all([labApi.getBrands(), storeApi.getAll()]);
      setBrands(brandResponse.data.data?.brands || []);
      setUnbrandedCount(brandResponse.data.data?.unbrandedCount || 0);
      setStores(storeResponse.data);
    } catch {
      setError('Nie udało się pobrać marek Laboratorium.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const activeCreatorStores = useMemo(
    () => stores.filter(store => store.isActive && store.engineEnabled && store.engineIntegrationId),
    [stores],
  );

  const handleCreate = async () => {
    if (!name.trim()) return;
    try {
      setSaving(true);
      setError(null);
      await labApi.createBrand({ name: name.trim(), storeId: storeId || null });
      setShowCreate(false);
      setName('');
      setStoreId('');
      await load();
      onCreated?.();
    } catch (createError) {
      const message = createError && typeof createError === 'object' && 'response' in createError
        ? (createError as { response?: { data?: { error?: string } } }).response?.data?.error
        : null;
      setError(message || 'Nie udało się utworzyć marki.');
    } finally {
      setSaving(false);
    }
  };

  const closeCreateModal = () => {
    setShowCreate(false);
    setError(null);
  };

  return (
    <div className={mode === 'button' ? 'contents' : 'space-y-6'}>
      {mode === 'page' && <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-text-primary">Laboratorium</h1>
          <p className="mt-2 text-text-secondary">Wybierz markę, aby przejść do jej produktów i wygenerowanych zasobów.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link to="/marketing/laboratorium/avatary" className="btn-secondary inline-flex items-center gap-2"><Users className="h-4 w-4" />Avatary UGC</Link>
          <Link to="/marketing/laboratorium/glosy" className="btn-secondary inline-flex items-center gap-2"><Mic2 className="h-4 w-4" />Głosy</Link>
          <button type="button" onClick={() => { setError(null); setShowCreate(true); }} className="btn-primary inline-flex items-center gap-2"><Plus className="h-4 w-4" />Dodaj markę</button>
        </div>
      </div>}

      {mode === 'button' && <button type="button" onClick={() => { setError(null); setShowCreate(true); }} className="flex items-center gap-2 px-4 py-2.5 bg-accent-primary/15 border border-accent-primary/30 rounded-lg text-accent-primary text-sm font-medium hover:bg-accent-primary/25 transition-colors"><Plus className="h-4 w-4" />Dodaj markę</button>}
      {mode !== 'button' && error && <p role="alert" className="rounded-lg border border-danger/25 bg-danger/10 p-3 text-sm text-danger">{error}</p>}

      {mode !== 'button' && (loading ? (
        <div className="flex min-h-48 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-accent-primary" /></div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {brands.map(brand => (
            <Link
              key={brand.id}
              to={`/marketing/laboratorium/marka/${encodeURIComponent(brand.id)}`}
              className="group rounded-card border border-border bg-surface-raised p-5 transition-colors hover:border-accent-primary/40 hover:bg-surface-hover/40"
            >
              <div className="mb-8 flex items-start justify-between gap-4">
                <div className="rounded-xl bg-accent-primary/10 p-3 text-accent-primary"><FlaskConical className="h-6 w-6" /></div>
                {brand.store && (
                  <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs ${brand.store.engineEnabled ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning'}`}>
                    <StoreIcon className="h-3 w-3" />{brand.store.name}
                  </span>
                )}
              </div>
              <h2 className="text-lg font-semibold text-text-primary">{brand.name}</h2>
              <p className="mt-1 text-sm text-text-muted">{brand.productCount} {brand.productCount === 1 ? 'produkt' : 'produktów'}</p>
            </Link>
          ))}

          <Link
            to="/marketing/laboratorium/bez-marki"
            className="group rounded-card border border-border bg-surface-raised p-5 transition-colors hover:border-accent-primary/40 hover:bg-surface-hover/40"
          >
            <div className="mb-8 flex items-start justify-between gap-4">
              <div className="rounded-xl bg-surface-hover p-3 text-text-secondary"><Tags className="h-6 w-6" /></div>
              <span className="rounded-full bg-surface-hover px-2.5 py-1 text-xs text-text-muted">Systemowa</span>
            </div>
            <h2 className="text-lg font-semibold text-text-primary">Bez marki</h2>
            <p className="mt-1 text-sm text-text-muted">{unbrandedCount} {unbrandedCount === 1 ? 'produkt' : 'produktów'}</p>
          </Link>
        </div>
      ))}

      <Modal
        isOpen={showCreate}
        onClose={closeCreateModal}
        title="Dodaj markę"
        description="Sklep jest opcjonalny. Jeśli go wybierzesz, będzie automatycznie używany przy wdrożeniu do Kreatora."
        size="md"
        footer={(
          <div className="flex items-center justify-between gap-4">
            <button type="button" onClick={handleCreate} disabled={saving || !name.trim()} className="btn-primary inline-flex items-center gap-2 disabled:opacity-50">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}Dodaj markę
            </button>
            <button type="button" onClick={closeCreateModal} className="btn-secondary">Anuluj</button>
          </div>
        )}
      >
        <div className="space-y-5">
          {error && <p role="alert" className="rounded-lg border border-danger/25 bg-danger/10 p-3 text-sm text-danger">{error}</p>}
          <div>
            <label className="mb-2 block text-sm font-medium text-text-primary">Nazwa marki</label>
            <input value={name} onChange={event => setName(event.target.value)} maxLength={120} autoFocus className="w-full rounded-lg border border-border bg-surface-hover px-3 py-2.5 text-sm text-text-primary focus:border-accent-primary focus:outline-none" />
          </div>
          <div>
            <label className="mb-2 block text-sm font-medium text-text-primary">Sklep Kreatora</label>
            <Select value={storeId} onChange={event => setStoreId(event.target.value)}>
              <option value="">Bez przypisanego sklepu</option>
              {activeCreatorStores.map(store => <option key={store.id} value={store.id}>{store.name}</option>)}
            </Select>
            {activeCreatorStores.length === 0 && <p className="mt-2 text-xs text-text-muted">Brak aktywnych sklepów z integracją Kreatora.</p>}
          </div>
        </div>
      </Modal>
    </div>
  );
}
